// Showing dates in the calendar the user picked (Settings → App → Calendar). Days are always
// stored as Gregorian 'YYYY-MM-DD'; only the display changes. ICU (Intl) draws most calendars;
// the Julian calendar is converted here because ICU has none. If a device's Intl lacks a
// calendar, dates fall back to Gregorian.
import type { Day } from '../types';

export const CALENDARS = ['gregorian', 'revisedJulian', 'julian', 'hebrew', 'islamicUmmAlQura', 'persian', 'indian', 'chineseLunar', 'coptic'] as const;
export type CalendarId = (typeof CALENDARS)[number];

// Revised Julian matches the Gregorian calendar until the year 2800.
const ICU: Record<Exclude<CalendarId, 'julian'>, string> = {
  gregorian: 'gregory', revisedJulian: 'gregory', hebrew: 'hebrew', islamicUmmAlQura: 'islamic-umalqura',
  persian: 'persian', indian: 'indian', chineseLunar: 'chinese', coptic: 'coptic',
};

const parse = (day: Day) => day.split('-').map(Number) as [number, number, number];

/** The Julian-calendar date of a Gregorian day (via the Julian Day Number). */
export function toJulian(day: Day): [year: number, month: number, day: number] {
  const [y, m, d] = parse(day);
  const a = Math.floor((14 - m) / 12);
  const yy = y + 4800 - a;
  const mm = m + 12 * a - 3;
  const jdn = d + Math.floor((153 * mm + 2) / 5) + 365 * yy + Math.floor(yy / 4) - Math.floor(yy / 100) + Math.floor(yy / 400) - 32045;
  const c = jdn + 32082;
  const dd = Math.floor((4 * c + 3) / 1461);
  const e = c - Math.floor((1461 * dd) / 4);
  const n = Math.floor((5 * e + 2) / 153);
  return [dd - 4800 + Math.floor(n / 10), n + 3 - 12 * Math.floor(n / 10), e - Math.floor((153 * n + 2) / 5) + 1];
}

const formatters = new Map<string, Intl.DateTimeFormat | null>();
function formatter(locale: string, calendar: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat | null {
  const key = `${locale}|${calendar}|${JSON.stringify(opts)}`;
  if (!formatters.has(key)) {
    let f: Intl.DateTimeFormat | null = null;
    try {
      f = new Intl.DateTimeFormat(`${locale.split('-u-')[0]}-u-ca-${calendar}`, opts);
      if (f.resolvedOptions().calendar !== calendar) f = null; // device without this calendar
    } catch {
      f = null;
    }
    formatters.set(key, f);
  }
  return formatters.get(key)!;
}

/** A Gregorian day as text in `calendar`, e.g. "Sat, 20 Heshvan" for 2026-10-31 in Hebrew. */
export function formatInCalendar(day: Day, opts: Intl.DateTimeFormatOptions, calendar: CalendarId, locale: string): string {
  const [y, m, d] = parse(day);
  const date = new Date(y, m - 1, d);
  if (calendar === 'julian') {
    const [jy, jm, jd] = toJulian(day);
    const { weekday, ...rest } = opts;
    const body = new Date(jy, jm - 1, jd).toLocaleDateString(locale, rest);
    return weekday ? `${date.toLocaleDateString(locale, { weekday })}, ${body}` : body;
  }
  // Chinese months have no short names ("Mo9"); the long form reads "Ninth Month".
  const shown = calendar === 'chineseLunar' && opts.month === 'short' ? { ...opts, month: 'long' as const } : opts;
  const f = calendar === 'gregorian' ? null : formatter(locale, ICU[calendar], shown);
  return f ? f.format(date) : date.toLocaleDateString(locale, opts);
}
